// /reset-password?token=<...> — public. Consumes the token from the reset
// email, POSTs the new password to /auth/reset-password, and auto-logs
// the operator in on success (Hono response includes a fresh JWT).

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { authStore } from '@/lib/auth-store';
import { ApiError, resetPassword } from '@/lib/api';
import { BrandMark } from '@/components/Brand';

export function ResetPassword() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/reset-password' as never }) as { token?: string };
  const token = search.token ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await resetPassword(token, password);
      // Hono's /auth/reset-password returns the same shape as /auth/login —
      // store the token + user and go straight to /dashboard.
      if (res.token) {
        authStore.setSession(res.token, res.user, res.exp);
        await navigate({ to: '/dashboard' as never, replace: true });
        return;
      }
      setError('Reset response missing token — please sign in.');
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        if (body?.error === 'invalid_token') setError('This reset link is invalid.');
        else if (body?.error === 'token_expired') setError('This reset link has expired. Request a new one.');
        else setError(err.message);
      } else if (err instanceof Error) setError(err.message);
      else setError('Reset failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--bo-bg)] px-4 py-12">
      <div className="w-full max-w-sm rounded-2xl border border-[color:var(--bo-line)] bg-white p-8 shadow-sm">
        {/* These pages are reached from an email link, where an unbranded
            page is exactly what a phishing warning looks like. */}
        <BrandMark />
        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Set a new password</h1>

        {tokenMissing ? (
          <div className="mt-6 rounded-lg border border-[color:var(--bo-line)] bg-[#fff1f2] p-4 text-[13px] text-[#9f1239]">
            No reset token in the URL. Open the link from your reset email — or
            <Link
              to={'/forgot-password' as never}
              className="ml-1 font-medium text-[color:var(--bo-accent)] hover:underline"
            >
              request a new one
            </Link>
            .
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="password" className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
                New password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[14px] outline-none focus:border-[color:var(--bo-accent)]"
              />
              <p className="mt-1 text-[11px] text-[color:var(--bo-mute)]">Minimum 8 characters.</p>
            </div>
            <div>
              <label htmlFor="confirm" className="block text-[12px] font-medium text-[color:var(--bo-ink)]">
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[14px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </div>
            {error ? (
              <p className="rounded-md bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-[color:var(--bo-ink)] text-[13.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Set password and sign in'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-[11.5px] text-[color:var(--bo-mute)]">
          <Link to={'/login' as never} className="font-medium text-[color:var(--bo-accent)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
